// Publieke retourpagina (/retour) — opdracht 20-07, blok A/D/E;
// shop-uitstraling designronde 20-07, polishronde: alles op één
// contentkolom en de details zoals de shop ze doet.
//
// Mobiel eerst en formulier-centraal, zoals een checkout. Header,
// USP-balk, Webwinkelkeur-blok en footer volgen de webshop
// (RetourShopElementen). Volledig toegankelijk: gekoppelde labels,
// fieldset/legend voor keuzegroepen, toetsenbord-navigeerbaar,
// aria-live-foutmeldingen, grote klikdoelen en focus op de stap-titel
// bij elke stapwissel.

// Shopfont lokaal gebundeld (Fontsource), zie .retour-shop in
// globals.css: Nunito Sans voor lopende tekst, Arimo voor koppen.
import "@fontsource/nunito-sans/400.css";
import "@fontsource/nunito-sans/600.css";
import "@fontsource/nunito-sans/700.css";
import "@fontsource/arimo/700.css";

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Camera, CheckCircle2, Heart } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  retourApi,
  vertaalRetourFout,
  type RetourAanmeldAntwoord,
  type RetourArtikelKeuze,
  type RetourInvulhulpAntwoord,
  type RetourLookupAntwoord,
  type RetourOplossingWaarde,
  type RetourRedenWaarde,
} from "@/lib/api";
import { RetourInvulhulp } from "./RetourInvulhulp";
import {
  NovaHulpKaart,
  ProductFoto,
  RETOUR_KAART,
  RETOUR_KOLOM,
  RETOUR_RASTER,
  RetourBeleidBlok,
  RetourFooter,
  RetourHeader,
  StappenIndicator,
  UspBalk,
  ZekerhedenStrip,
} from "./RetourShopElementen";
import {
  ALLE_REDENEN,
  GEOPEND_GEVOELIGE_REDENEN,
  SCHADE_REDENEN,
  artikelCompleet,
  artikelUitkomst,
  formatteerBedrag,
  pakketOordeel,
  type ArtikelFormulier,
} from "./retourLogica";

// Gratis voedingsadvies voor de "mijn dier lust het niet"-route
// (designronde 29-07): zacht, niet-blokkerend aanbod in de reden-stap.
const VOERWIJZER_URL = "https://www.voeronline.nl/voerwijzer/";

type Stap = "zoeken" | "artikelen" | "redenen" | "overzicht" | "klaar";

const STAP_NUMMER: Record<Exclude<Stap, "klaar">, number> = {
  zoeken: 1,
  artikelen: 2,
  redenen: 3,
  overzicht: 4,
};

// Volgorde voor de klikbare stepper: afgeronde stappen navigeren terug.
const STAPPEN_VOLGORDE: Exclude<Stap, "klaar">[] = [
  "zoeken",
  "artikelen",
  "redenen",
  "overzicht",
];

const VERWACHTING_KEUZES = [
  "dier_lust_niet",
  "kwaliteit",
  "inhoud",
  "anders",
] as const;

const VERWACHTING_LABELS: Record<string, string> = {
  dier_lust_niet: "retour.stap3.verwachting_dier_lust_niet",
  kwaliteit: "retour.stap3.verwachting_kwaliteit",
  inhoud: "retour.stap3.verwachting_inhoud",
  anders: "retour.stap3.verwachting_anders",
};

export function RetourPagina() {
  const { t } = useTranslation();

  const [stap, setStap] = useState<Stap>("zoeken");
  const [lookup, setLookup] = useState<RetourLookupAntwoord | null>(null);
  const [selectie, setSelectie] = useState<Record<number, number>>({});
  const [formulieren, setFormulieren] = useState<ArtikelFormulier[]>([]);
  const [oplossing, setOplossing] = useState<RetourOplossingWaarde | "">("");
  // Optioneel opmerkingenveld op retourniveau (opdracht 21-07): landt
  // in het retourrecord, het klantdossier en op de retourpakbon.
  const [opmerking, setOpmerking] = useState("");
  const [resultaat, setResultaat] = useState<RetourAanmeldAntwoord | null>(
    null,
  );
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);
  const [openVragen, setOpenVragen] = useState<string[]>([]);
  const [voorstelToegepast, setVoorstelToegepast] = useState(false);

  // Elke stap begint bovenaan (header en kop in beeld); de focus gaat
  // zonder mee-scrollen naar de stap-titel voor screenreaders.
  const stapTitelRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    stapTitelRef.current?.focus({ preventScroll: true });
  }, [stap]);

  // ── Stap 1: zoeken ──────────────────────────────────────────────
  // De retour-ingang in het account linkt met ?ordernummer=ORD26… mee
  // (plan 01-09, A1). Alleen het ordernummer staat in de link, nooit
  // het e-mailadres: dat is juist de tweede sleutel die de lookup
  // beschermt, en persoonsgegevens horen niet in een url. We vullen
  // dus alleen het veld voor en zetten de focus op het e-mailveld;
  // er wordt niets automatisch opgezocht.
  const [searchParams] = useSearchParams();
  const ordernummerUitLink = (searchParams.get("ordernummer") ?? "").trim();

  const [ordernummer, setOrdernummer] = useState(ordernummerUitLink);
  const [email, setEmail] = useState("");

  // Na de stap-focus hierboven (die bij het monteren de stap-titel
  // pakt) de focus alsnog naar het e-mailveld, maar alleen als de link
  // een ordernummer meebracht. Eenmalig bij het monteren.
  const emailVeldRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ordernummerUitLink) {
      emailVeldRef.current?.focus({ preventScroll: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function zoekOrder(e: React.FormEvent) {
    e.preventDefault();
    setFout(null);
    setBezig(true);
    try {
      const antwoord = await retourApi.lookup(ordernummer.trim(), email.trim());
      setLookup(antwoord);
      setSelectie({});
      setFormulieren([]);
      setStap("artikelen");
    } catch (err) {
      setFout(vertaalRetourFout(err, t("retour.fout_server")));
    } finally {
      setBezig(false);
    }
  }

  // ── Stap 2 → 3 ─────────────────────────────────────────────────
  function naarRedenen() {
    if (!lookup) return;
    const gekozen = Object.entries(selectie).filter(([, aantal]) => aantal > 0);
    if (gekozen.length === 0) {
      setFout(t("retour.stap2.selecteer_minstens_een"));
      return;
    }
    setFout(null);
    setFormulieren((huidige) =>
      gekozen.map(([indexStr, aantal]) => {
        const index = Number(indexStr);
        const bestaand = huidige.find((f) => f.index === index);
        if (bestaand) {
          return { ...bestaand, aantal };
        }
        return {
          index,
          aantal,
          reden: "" as const,
          verpakkingGeopend: null,
          toelichting: "",
          toelichtingKeuze: "",
          omschrijvingDuidelijk: null,
          bijlageIds: [],
        };
      }),
    );
    setStap("redenen");
  }

  // Blok D: het AI-voorstel toepassen als voorinvulling — de klant
  // doorloopt daarna gewoon de stappen en bevestigt alles zelf.
  function pasVoorstelToe(voorstel: RetourInvulhulpAntwoord) {
    if (!lookup) return;
    const nieuweSelectie: Record<number, number> = {};
    const nieuweFormulieren: ArtikelFormulier[] = [];
    for (const rij of voorstel.artikelen) {
      const artikel = lookup.artikelen[rij.artikel_index];
      if (!artikel) continue;
      nieuweSelectie[rij.artikel_index] = Math.min(
        rij.aantal,
        artikel.aantal,
      );
      nieuweFormulieren.push({
        index: rij.artikel_index,
        aantal: Math.min(rij.aantal, artikel.aantal),
        reden: rij.reden,
        verpakkingGeopend: rij.verpakking_geopend,
        toelichting: rij.toelichting ?? "",
        toelichtingKeuze: "",
        omschrijvingDuidelijk: null,
        bijlageIds: [],
      });
    }
    setOpenVragen(voorstel.open_vragen);
    setVoorstelToegepast(nieuweFormulieren.length > 0);
    if (nieuweFormulieren.length > 0) {
      setSelectie(nieuweSelectie);
      setFormulieren(nieuweFormulieren);
      setStap("redenen");
    }
  }

  function werkFormulierBij(
    index: number,
    wijziging: Partial<ArtikelFormulier>,
  ) {
    setFormulieren((huidige) =>
      huidige.map((f) => (f.index === index ? { ...f, ...wijziging } : f)),
    );
  }

  // ── Stap 3 → 4 ─────────────────────────────────────────────────
  const allesCompleet =
    formulieren.length > 0 && formulieren.every(artikelCompleet);
  const oordeel = allesCompleet ? pakketOordeel(formulieren) : null;

  // Verrekening-preview voor de bevestigingsstap (designronde 29-07),
  // als spiegel van de kaart op de retourstatuspagina: uitgesloten
  // artikelen tellen niet mee, retourkosten alleen bij kosten_klant.
  // De server herrekent alles bij het indienen.
  const artikelbedragCents = lookup
    ? formulieren
        .filter((f) => artikelUitkomst(f) !== "uitgesloten")
        .reduce(
          (som, f) =>
            som + (lookup.artikelen[f.index]?.prijs_cents ?? 0) * f.aantal,
          0,
        )
    : 0;
  const verrekeningCents =
    oordeel === "kosten_klant" && lookup ? lookup.retourkosten_cents : 0;
  const alleGekozenOpVoorraad =
    lookup !== null &&
    formulieren.every(
      (f) => lookup.artikelen[f.index]?.op_voorraad === true,
    );

  function naarOverzicht() {
    if (!allesCompleet) {
      setFout(t("retour.fout_algemeen"));
      return;
    }
    if (oordeel !== "niet_toegestaan" && oplossing === "") {
      setFout(t("retour.stap3.oplossing_titel"));
      return;
    }
    setFout(null);
    setStap("overzicht");
  }

  // ── Stap 4: bevestigen ─────────────────────────────────────────
  async function bevestig() {
    if (!lookup) return;
    setFout(null);
    setBezig(true);
    try {
      const artikelen: RetourArtikelKeuze[] = formulieren.map((f) => ({
        artikel_index: f.index,
        aantal: f.aantal,
        reden: f.reden as RetourRedenWaarde,
        verpakking_geopend: f.verpakkingGeopend,
        toelichting: f.toelichting.trim() || null,
        toelichting_keuze: f.toelichtingKeuze || null,
        omschrijving_duidelijk: f.omschrijvingDuidelijk,
        bijlage_ids: f.bijlageIds,
      }));
      const antwoord = await retourApi.aanmelden(
        lookup.token,
        artikelen,
        (oplossing || "terugbetaling") as RetourOplossingWaarde,
        opmerking.trim() || null,
      );
      setResultaat(antwoord);
      setStap("klaar");
    } catch (err) {
      setFout(vertaalRetourFout(err, t("retour.fout_server")));
    } finally {
      setBezig(false);
    }
  }

  // ── Weergave ───────────────────────────────────────────────────
  return (
    <div className="retour-shop flex min-h-screen flex-col bg-white">
      <RetourHeader />
      <UspBalk />

      <main className={`${RETOUR_KOLOM} flex-1 py-8`}>
        <section aria-label={t("retour.titel")}>
          {/* Kruisverwijzing (designronde 29-07): wie al een retour
              heeft aangemeld, hoort meteen de weg naar /RetourStatus. */}
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <h1 className="text-2xl font-bold tracking-tight text-brand-navy">
              {t("retour.titel")}
            </h1>
            <p className="text-[13.5px] text-gray-500">
              {t("retour.volg_vraag")}{" "}
              <Link
                to="/RetourStatus"
                className="rounded-sm font-medium text-blue-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t("retour.volg_link")}
              </Link>
            </p>
          </div>
          <StappenIndicator
            huidige={stap === "klaar" ? 4 : STAP_NUMMER[stap]}
            totaal={4}
            onNaarStap={
              stap === "klaar"
                ? undefined
                : (nummer) => {
                    const doel = STAPPEN_VOLGORDE[nummer - 1];
                    if (doel) {
                      setFout(null);
                      setStap(doel);
                    }
                  }
            }
          />
          {stap === "zoeken" && <p className="mt-3 text-[14px]">{t("retour.intro")}</p>}
          {stap === "zoeken" && <ZekerhedenStrip />}

          {fout && (
            <div
              role="alert"
              aria-live="polite"
              className="mt-4 rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive"
            >
              {fout}
            </div>
          )}
          {/* Bij een mislukte lookup geen kale foutregel: Nova zoekt
              direct met de klant mee (designronde 29-07). */}
          {fout && stap === "zoeken" && (
            <NovaHulpKaart tekst={t("retour.nova.fout_tekst")} />
          )}

          {/* Desktop: formulier met het retourbeleid ernaast; mobiel
              komt het beleid-blok onder de stappen te staan. */}
          <div className={`mt-6 ${RETOUR_RASTER}`}>
          <div className="min-w-0">
            {stap === "zoeken" && (
              <form
                onSubmit={zoekOrder}
                noValidate
                className={`${RETOUR_KAART} space-y-4`}
              >
                {/* De paginakop zegt het al — kop alleen voor
                    screenreaders en het focusdoel bij stapwissel. */}
                <h2
                  ref={stapTitelRef}
                  tabIndex={-1}
                  className="sr-only"
                >
                  {t("retour.stap1.titel")}
                </h2>
                <div className="space-y-1.5">
                  <Label htmlFor="retour-ordernummer">
                    {t("retour.stap1.ordernummer")}
                  </Label>
                  <Input
                    id="retour-ordernummer"
                    name="ordernummer"
                    placeholder={t("retour.stap1.ordernummer_placeholder")}
                    autoComplete="off"
                    inputMode="text"
                    required
                    value={ordernummer}
                    onChange={(e) => setOrdernummer(e.target.value)}
                    aria-describedby="retour-ordernummer-hulp"
                    className="h-11 bg-white"
                  />
                  {/* Altijd zichtbare hulptekst, zelfde bewoordingen
                      als de retourstatuspagina (designronde 29-07). */}
                  <p
                    id="retour-ordernummer-hulp"
                    className="text-[12.5px] text-muted-foreground"
                  >
                    {t("retour.stap1.ordernummer_hulp")}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="retour-email">{t("retour.stap1.email")}</Label>
                  <Input
                    id="retour-email"
                    ref={emailVeldRef}
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-describedby="retour-email-hulp"
                    className="h-11 bg-white"
                  />
                  <p
                    id="retour-email-hulp"
                    className="text-[12.5px] text-muted-foreground"
                  >
                    {t("retour.stap1.email_hulp")}
                  </p>
                </div>
                <Button
                  type="submit"
                  className="h-11 w-full text-[15px]"
                  disabled={bezig}
                >
                  {bezig && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  )}
                  {bezig ? t("retour.stap1.bezig") : t("retour.stap1.zoeken")}
                </Button>
              </form>
            )}

            {stap === "artikelen" && lookup && (
              <div className={`${RETOUR_KAART} space-y-4`}>
                <h2
                  ref={stapTitelRef}
                  tabIndex={-1}
                  className="text-lg font-semibold outline-none"
                >
                  {t("retour.stap2.titel")}
                </h2>
                <p className="text-[13px] text-muted-foreground">
                  {lookup.ordernummer} ·{" "}
                  {t("retour.stap2.besteld_op", {
                    datum: new Date(lookup.besteldatum).toLocaleDateString(
                      "nl-NL",
                    ),
                  })}
                </p>

                {/* Blok 2 (besluit JP 19-10): de termijn blokkeert hier
                    niet meer. Buiten de termijn, een onzekere bezorgdatum
                    of een zakelijke klant zet de aanmelding door naar de
                    klantenservice ("wij kijken ernaar"). */}
                {lookup.artikelen.length === 0 ? (
                  // Leegstaat: de order is gevonden maar levert geen
                  // artikelen op — verwijs naar de klantenservice in
                  // plaats van een kale kaart.
                  <>
                    <p className="text-[14.5px] font-semibold">
                      {t("retour.stap2.leeg_titel")}
                    </p>
                    <p className="rounded-md bg-brand-cyan-soft p-4 text-sm">
                      {t("retour.stap2.leeg_tekst")}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 w-full"
                      onClick={() => setStap("zoeken")}
                    >
                      {t("retour.stap2.terug")}
                    </Button>
                  </>
                ) : (
                  // Volgorde uit het referentiebestand: productregels
                  // bovenaan, daaronder de invulhulp, daaronder de
                  // knoppenrij Terug en Verder.
                  <>
                    {lookup.bestaande_retournummers.length > 0 && (
                      <p className="rounded-md border border-brand-cyan/40 bg-brand-cyan-soft p-3 text-[13px]">
                        {t("retour.bestaande_retour")}{" "}
                        {lookup.bestaande_retournummers.join(", ")}
                      </p>
                    )}
                    <ul className="space-y-2.5" role="list">
                      {lookup.artikelen.map((artikel) => {
                        const gekozen = selectie[artikel.index] ?? 0;
                        return (
                          <li
                            key={artikel.index}
                            className="flex flex-wrap items-center gap-3.5 rounded-xl border bg-white px-3.5 py-3 hover:border-slate-300"
                          >
                            <input
                              type="checkbox"
                              id={`artikel-${artikel.index}`}
                              className="h-5 w-5 shrink-0 accent-primary"
                              checked={gekozen > 0}
                              onChange={(e) =>
                                setSelectie((s) => ({
                                  ...s,
                                  [artikel.index]: e.target.checked ? 1 : 0,
                                }))
                              }
                            />
                            <ProductFoto url={artikel.afbeelding_url} />
                            <label
                              htmlFor={`artikel-${artikel.index}`}
                              className="min-w-0 flex-1 basis-44 cursor-pointer"
                            >
                              <span className="block text-[15.5px] font-bold text-brand-navy">
                                {artikel.naam}
                              </span>
                              <span className="block text-[13.5px] text-muted-foreground">
                                {t("retour.stap2.besteld_aantal", {
                                  aantal: artikel.aantal,
                                })}{" "}
                                ·{" "}
                                {t("retour.stap2.prijs_per_stuk", {
                                  prijs: formatteerBedrag(artikel.prijs_cents),
                                })}
                              </span>
                            </label>
                            <div
                              role="group"
                              aria-label={t("retour.stap2.aantal_label")}
                              className="ml-auto flex shrink-0 items-center overflow-hidden rounded-[9px] border border-input bg-white"
                            >
                              <button
                                type="button"
                                aria-label={t("retour.stap2.aantal_minder", {
                                  naam: artikel.naam,
                                })}
                                onClick={() =>
                                  setSelectie((s) => ({
                                    ...s,
                                    [artikel.index]: Math.max(0, gekozen - 1),
                                  }))
                                }
                                className="grid h-[38px] w-[34px] place-items-center text-[17px] text-brand-navy hover:bg-muted/60"
                              >
                                −
                              </button>
                              <b
                                aria-live="polite"
                                className="w-[34px] text-center text-[15px]"
                              >
                                {gekozen}
                              </b>
                              <button
                                type="button"
                                aria-label={t("retour.stap2.aantal_meer", {
                                  naam: artikel.naam,
                                })}
                                onClick={() =>
                                  setSelectie((s) => ({
                                    ...s,
                                    [artikel.index]: Math.min(
                                      artikel.aantal,
                                      gekozen + 1,
                                    ),
                                  }))
                                }
                                className="grid h-[38px] w-[34px] place-items-center text-[17px] text-brand-navy hover:bg-muted/60"
                              >
                                +
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    <RetourInvulhulp
                      token={lookup.token}
                      onVoorstel={pasVoorstelToe}
                    />
                    <div className="flex gap-3 pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="h-11 flex-1"
                        onClick={() => setStap("zoeken")}
                      >
                        {t("retour.stap2.terug")}
                      </Button>
                      <Button
                        type="button"
                        className="h-11 flex-1"
                        onClick={naarRedenen}
                      >
                        {t("retour.stap2.verder")}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {stap === "redenen" && lookup && (
              <div className="space-y-4">
                <h2
                  ref={stapTitelRef}
                  tabIndex={-1}
                  className="text-lg font-semibold outline-none"
                >
                  {t("retour.stap3.titel")}
                </h2>
                {voorstelToegepast && (
                  <div
                    aria-live="polite"
                    className="rounded-md border border-brand-cyan/40 bg-brand-cyan-soft p-3 text-[13px]"
                  >
                    <p className="font-medium">
                      {t("retour.invulhulp.voorstel_toegepast")}
                    </p>
                    {openVragen.length > 0 && (
                      <ul className="mt-1 list-disc pl-4">
                        {openVragen.map((vraag) => (
                          <li key={vraag}>{vraag}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                {formulieren.map((formulier) => (
                  <ArtikelVragen
                    key={formulier.index}
                    formulier={formulier}
                    naam={lookup.artikelen[formulier.index]?.naam ?? ""}
                    token={lookup.token}
                    onWijzig={(w) => werkFormulierBij(formulier.index, w)}
                  />
                ))}

                {allesCompleet && oordeel !== "niet_toegestaan" && (
                  <fieldset className={RETOUR_KAART}>
                    <legend className="px-1 text-[15px] font-semibold">
                      {t("retour.stap3.oplossing_titel")}
                    </legend>
                    <div className="mt-2 space-y-2">
                      <OplossingOptie
                        id="oplossing-kortingscode"
                        gekozen={oplossing === "kortingscode"}
                        onKies={() => setOplossing("kortingscode")}
                        titel={t("retour.stap3.oplossing_kortingscode")}
                        label={t("retour.stap3.oplossing_kortingscode_label")}
                      />
                      <OplossingOptie
                        id="oplossing-terugbetaling"
                        gekozen={oplossing === "terugbetaling"}
                        onKies={() => setOplossing("terugbetaling")}
                        titel={t("retour.stap3.oplossing_terugbetaling")}
                        label={t("retour.stap3.oplossing_terugbetaling_label")}
                      />
                      {alleGekozenOpVoorraad && (
                        <OplossingOptie
                          id="oplossing-vervanging"
                          gekozen={oplossing === "vervanging"}
                          onKies={() => setOplossing("vervanging")}
                          titel={t("retour.stap3.oplossing_vervanging")}
                          label={t("retour.stap3.oplossing_vervanging_label")}
                        />
                      )}
                    </div>
                  </fieldset>
                )}

                {allesCompleet && oordeel !== "niet_toegestaan" && (
                  <div className={`${RETOUR_KAART} space-y-1.5`}>
                    <Label htmlFor="retour-opmerking">
                      {t("retour.stap3.opmerking_label")}
                    </Label>
                    <Textarea
                      id="retour-opmerking"
                      className="bg-white"
                      value={opmerking}
                      maxLength={2000}
                      onChange={(e) => setOpmerking(e.target.value)}
                      aria-describedby="retour-opmerking-hulp"
                    />
                    <p
                      id="retour-opmerking-hulp"
                      className="text-[12.5px] text-muted-foreground"
                    >
                      {t("retour.stap3.opmerking_hulp")}
                    </p>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 flex-1"
                    onClick={() => setStap("artikelen")}
                  >
                    {t("retour.stap3.terug")}
                  </Button>
                  <Button
                    type="button"
                    className="h-11 flex-1"
                    disabled={
                      !allesCompleet ||
                      (oordeel !== "niet_toegestaan" && oplossing === "")
                    }
                    onClick={naarOverzicht}
                  >
                    {t("retour.stap3.verder")}
                  </Button>
                </div>
              </div>
            )}

            {stap === "overzicht" && lookup && (
              <div className={`${RETOUR_KAART} space-y-4`}>
                <h2
                  ref={stapTitelRef}
                  tabIndex={-1}
                  className="text-lg font-semibold outline-none"
                >
                  {t("retour.stap4.titel")}
                </h2>
                {/* Zelfde kaartstijl als de verrekening op de
                    retourstatuspagina (designronde 29-07): artikelen
                    met bedragen en de eerlijke kosten-preview vóór het
                    bevestigen. */}
                <div>
                  <h3 className="text-[15px] font-bold text-gray-900">
                    {t("retour.stap4.artikelen_titel")}
                  </h3>
                  <div className="mt-1">
                    {formulieren.map((f, index) => {
                      const artikel = lookup.artikelen[f.index];
                      const uitgesloten =
                        artikelUitkomst(f) === "uitgesloten";
                      return (
                        <div
                          key={f.index}
                          className={`flex justify-between gap-3.5 py-3.5 text-[15px] ${
                            index === formulieren.length - 1
                              ? ""
                              : "border-b border-gray-200"
                          }`}
                        >
                          <div className="min-w-0">
                            <div className="font-bold text-gray-900">
                              {artikel?.naam}
                              {f.aantal > 1 && (
                                <span className="ml-1.5 font-normal text-gray-500">
                                  {t("retour.statuspagina.artikel_aantal", {
                                    aantal: f.aantal,
                                  })}
                                </span>
                              )}
                            </div>
                            <div className="text-[13.5px] text-gray-500">
                              {t("retour.stap4.reden_voorvoegsel", {
                                reden: f.reden
                                  ? t(`retour.redenen.${f.reden}`)
                                  : "",
                              })}
                              {uitgesloten && (
                                <>
                                  {" · "}
                                  {t("retour.stap4.artikel_uitgesloten")}
                                </>
                              )}
                            </div>
                          </div>
                          <div className="whitespace-nowrap font-bold text-[#333333]">
                            {formatteerBedrag(
                              (artikel?.prijs_cents ?? 0) * f.aantal,
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {oordeel !== "niet_toegestaan" && (
                    <>
                      <div className="flex justify-between border-t border-gray-200 pt-2.5 text-[15px] text-[#333333]">
                        <span>{t("retour.statuspagina.artikelbedrag")}</span>
                        <span>{formatteerBedrag(artikelbedragCents)}</span>
                      </div>
                      {verrekeningCents > 0 && (
                        <div className="flex justify-between pt-2.5 text-[15px] text-gray-500">
                          <span>
                            {t("retour.statuspagina.retourkosten_verrekend")}
                          </span>
                          <span>− {formatteerBedrag(verrekeningCents)}</span>
                        </div>
                      )}
                      {oordeel !== "beoordeling" && (
                        <div className="mt-2.5 flex justify-between border-t-2 border-gray-900 pt-3 text-[16px] font-bold text-gray-900">
                          <span>
                            {oplossing === "kortingscode"
                              ? t(
                                  "retour.statuspagina.terug_totaal_kortingscode",
                                )
                              : t("retour.statuspagina.terug_totaal")}
                          </span>
                          <span>
                            {formatteerBedrag(
                              Math.max(
                                0,
                                artikelbedragCents - verrekeningCents,
                              ),
                            )}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="rounded-md border border-border bg-white p-4">
                  <h3 className="text-[14px] font-semibold">
                    {t("retour.stap4.kosten_titel")}
                  </h3>
                  <p className="mt-1 text-[13.5px]">
                    {oordeel === "gratis" && t("retour.stap4.kosten_gratis")}
                    {oordeel === "kosten_klant" &&
                      t("retour.stap4.kosten_klant", {
                        bedrag: formatteerBedrag(lookup.retourkosten_cents),
                      })}
                    {oordeel === "beoordeling" &&
                      t("retour.stap4.kosten_beoordeling")}
                    {oordeel === "niet_toegestaan" &&
                      t("retour.resultaat.geen_retour_titel")}
                  </p>
                </div>

                {oordeel !== "niet_toegestaan" && oplossing !== "" && (
                  <p className="text-[13.5px]">
                    <span className="font-semibold">
                      {t("retour.stap4.oplossing_titel")}:
                    </span>{" "}
                    {t(`retour.stap3.oplossing_${oplossing}`)}
                  </p>
                )}

                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 flex-1"
                    onClick={() => setStap("redenen")}
                    disabled={bezig}
                  >
                    {t("retour.stap4.terug")}
                  </Button>
                  <Button
                    type="button"
                    className="h-11 flex-1"
                    onClick={bevestig}
                    disabled={bezig}
                  >
                    {bezig && (
                      <Loader2
                        className="mr-2 h-4 w-4 animate-spin"
                        aria-hidden
                      />
                    )}
                    {bezig
                      ? t("retour.stap4.bezig")
                      : t("retour.stap4.bevestigen")}
                  </Button>
                </div>
              </div>
            )}

            {stap === "klaar" && resultaat && lookup && (
              <Resultaat
                resultaat={resultaat}
                token={lookup.token}
                titelRef={stapTitelRef}
              />
            )}
          </div>
          <div className="mt-6 md:mt-0">
            <RetourBeleidBlok
              retourkostenCents={lookup?.retourkosten_cents}
            />
          </div>
          </div>

          <NovaHulpKaart />
        </section>
      </main>

      <RetourFooter />
    </div>
  );
}

// ── Subcomponenten ───────────────────────────────────────────────

function OplossingOptie({
  id,
  gekozen,
  onKies,
  titel,
  label,
}: {
  id: string;
  gekozen: boolean;
  onKies: () => void;
  titel: string;
  label: string;
}) {
  return (
    <label
      htmlFor={id}
      className={`flex min-h-[56px] cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${
        gekozen
          ? "border-primary bg-brand-orange-50"
          : "bg-white hover:bg-muted/60"
      }`}
    >
      <input
        type="radio"
        id={id}
        name="oplossing"
        className="h-5 w-5 accent-primary"
        checked={gekozen}
        onChange={onKies}
      />
      <span>
        <span className="block text-[14px] font-medium">{titel}</span>
        <span className="block text-[12.5px] text-muted-foreground">
          {label}
        </span>
      </span>
    </label>
  );
}

function JaNeeKeuze({
  naam,
  vraag,
  jaTekst,
  neeTekst,
  waarde,
  onKies,
}: {
  naam: string;
  vraag: string;
  jaTekst: string;
  neeTekst: string;
  waarde: boolean | null;
  onKies: (w: boolean) => void;
}) {
  return (
    <fieldset>
      <legend className="text-[13.5px] font-medium">{vraag}</legend>
      <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
        {[
          { tekst: jaTekst, w: true },
          { tekst: neeTekst, w: false },
        ].map(({ tekst, w }) => (
          <label
            key={String(w)}
            className={`flex min-h-[48px] cursor-pointer items-center gap-2.5 rounded-lg border p-3 text-[13.5px] ${
              waarde === w
                ? "border-primary bg-brand-orange-50"
                : "bg-white hover:bg-muted/60"
            }`}
          >
            <input
              type="radio"
              name={naam}
              className="h-5 w-5 accent-primary"
              checked={waarde === w}
              onChange={() => onKies(w)}
            />
            {tekst}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function ArtikelVragen({
  formulier,
  naam,
  token,
  onWijzig,
}: {
  formulier: ArtikelFormulier;
  naam: string;
  token: string;
  onWijzig: (w: Partial<ArtikelFormulier>) => void;
}) {
  const { t } = useTranslation();
  const [uploadBezig, setUploadBezig] = useState(false);
  const [uploadFout, setUploadFout] = useState<string | null>(null);
  const [uploadNamen, setUploadNamen] = useState<string[]>([]);
  const bestandRef = useRef<HTMLInputElement>(null);

  const reden = formulier.reden;
  const isSchade = reden !== "" && SCHADE_REDENEN.includes(reden);
  const vraagGeopend =
    reden !== "" && GEOPEND_GEVOELIGE_REDENEN.includes(reden);
  const isVoldoetNiet = reden === "voldoet_niet_aan_verwachtingen";
  const isAndereReden = reden === "andere_reden";

  async function uploadFoto(e: React.ChangeEvent<HTMLInputElement>) {
    const bestand = e.target.files?.[0];
    if (!bestand) return;
    setUploadFout(null);
    setUploadBezig(true);
    try {
      const antwoord = await retourApi.uploadBijlage(token, bestand);
      onWijzig({
        bijlageIds: [...formulier.bijlageIds, antwoord.bijlage_id],
      });
      setUploadNamen((n) => [...n, bestand.name]);
      if (antwoord.ai_bruikbaar === false && antwoord.ai_feedback) {
        setUploadFout(antwoord.ai_feedback);
      }
    } catch {
      setUploadFout(t("retour.stap3.foto_geweigerd"));
    } finally {
      setUploadBezig(false);
      if (bestandRef.current) bestandRef.current.value = "";
    }
  }

  const redenSelectId = `reden-${formulier.index}`;
  const toelichtingId = `toelichting-${formulier.index}`;

  return (
    <div className={`${RETOUR_KAART} space-y-4`}>
      <h3 className="text-[15px] font-semibold">
        {formulier.aantal}x {naam}
      </h3>

      <div className="space-y-1.5">
        <Label htmlFor={redenSelectId}>{t("retour.stap3.reden_label")}</Label>
        <select
          id={redenSelectId}
          className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm"
          value={reden}
          onChange={(e) =>
            onWijzig({
              reden: e.target.value as ArtikelFormulier["reden"],
              verpakkingGeopend: null,
              toelichtingKeuze: "",
              omschrijvingDuidelijk: null,
            })
          }
        >
          <option value="" disabled>
            {t("retour.stap3.reden_kies")}
          </option>
          {ALLE_REDENEN.map((r) => (
            <option key={r} value={r}>
              {t(`retour.redenen.${r}`)}
            </option>
          ))}
        </select>
      </div>

      {vraagGeopend && (
        <JaNeeKeuze
          naam={`geopend-${formulier.index}`}
          vraag={t("retour.stap3.geopend_vraag")}
          jaTekst={t("retour.stap3.geopend_ja")}
          neeTekst={t("retour.stap3.geopend_nee")}
          waarde={formulier.verpakkingGeopend}
          onKies={(w) => onWijzig({ verpakkingGeopend: w })}
        />
      )}

      {isVoldoetNiet && (
        <>
          <div className="space-y-1.5">
            <Label htmlFor={`verwachting-${formulier.index}`}>
              {t("retour.stap3.verwachting_keuze_label")}
            </Label>
            <select
              id={`verwachting-${formulier.index}`}
              className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm"
              value={formulier.toelichtingKeuze}
              onChange={(e) => onWijzig({ toelichtingKeuze: e.target.value })}
            >
              <option value="" disabled>
                {t("retour.stap3.reden_kies")}
              </option>
              {VERWACHTING_KEUZES.map((k) => (
                <option key={k} value={k}>
                  {t(VERWACHTING_LABELS[k])}
                </option>
              ))}
            </select>
          </div>
          {/* Zacht, niet-blokkerend aanbod bij "mijn dier lust het
              niet" (designronde 29-07): gratis voedingsadvies via de
              VoerWijzer, in de kaderstijl van de Lightspeed-pagina. */}
          {formulier.toelichtingKeuze === "dier_lust_niet" && (
            <div className="flex items-start gap-2.5 rounded-[10px] border border-blue-100 bg-blue-50 px-4 py-3 text-[13.5px] text-[#333333]">
              <Heart
                className="mt-0.5 h-4 w-4 shrink-0 text-brand-cyan"
                aria-hidden
              />
              <span>
                {t("retour.stap3.voerwijzer_tekst")}{" "}
                <a
                  href={VOERWIJZER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-sm font-medium text-blue-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t("retour.stap3.voerwijzer_link")}
                </a>
              </span>
            </div>
          )}
          <JaNeeKeuze
            naam={`omschrijving-${formulier.index}`}
            vraag={t("retour.stap3.omschrijving_vraag")}
            jaTekst={t("retour.stap3.omschrijving_ja")}
            neeTekst={t("retour.stap3.omschrijving_nee")}
            waarde={formulier.omschrijvingDuidelijk}
            onKies={(w) => onWijzig({ omschrijvingDuidelijk: w })}
          />
        </>
      )}

      {(isSchade || isAndereReden || isVoldoetNiet || (vraagGeopend && !isAndereReden)) && (
        <div className="space-y-1.5">
          <Label htmlFor={toelichtingId}>
            {t("retour.stap3.toelichting_label")}
          </Label>
          <Textarea
            id={toelichtingId}
            className="bg-white"
            value={formulier.toelichting}
            placeholder={t("retour.stap3.toelichting_placeholder")}
            onChange={(e) => onWijzig({ toelichting: e.target.value })}
            aria-describedby={`${toelichtingId}-hulp`}
          />
          {(isSchade || isAndereReden) && (
            <p
              id={`${toelichtingId}-hulp`}
              className="text-[12.5px] text-muted-foreground"
            >
              {t("retour.stap3.toelichting_verplicht_hulp")}
            </p>
          )}
        </div>
      )}

      {isSchade && (
        <div className="space-y-2">
          <p className="text-[13.5px] font-medium">
            {t("retour.stap3.fotos_label")}
          </p>
          {/* Designronde 29-07: benoemen dat een foto de afhandeling
              versnelt — de upload bestond al. */}
          <p className="text-[12.5px] text-muted-foreground">
            {t("retour.stap3.foto_versnelt_hint")}
          </p>
          <div className="rounded-md border border-border bg-white p-3 text-[12.5px]">
            <p className="font-medium">
              {t("retour.stap3.foto_instructies_titel")}
            </p>
            <ul className="mt-1 list-disc pl-4 space-y-0.5">
              <li>{t("retour.stap3.foto_instructie_1")}</li>
              <li>{t("retour.stap3.foto_instructie_2")}</li>
              <li>{t("retour.stap3.foto_instructie_3")}</li>
              <li>{t("retour.stap3.foto_instructie_4")}</li>
            </ul>
          </div>
          <input
            ref={bestandRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="sr-only"
            id={`foto-${formulier.index}`}
            onChange={uploadFoto}
          />
          <Button
            type="button"
            variant="outline"
            className="h-11"
            disabled={uploadBezig}
            onClick={() => bestandRef.current?.click()}
          >
            {uploadBezig ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Camera className="mr-2 h-4 w-4" aria-hidden />
            )}
            {uploadBezig
              ? t("retour.stap3.foto_bezig")
              : t("retour.stap3.foto_toevoegen")}
          </Button>
          {uploadNamen.length > 0 && (
            <ul className="text-[12.5px] text-muted-foreground" role="list">
              {uploadNamen.map((n, i) => (
                <li key={`${n}-${i}`} className="flex items-center gap-1.5">
                  <CheckCircle2
                    className="h-3.5 w-3.5 text-green-600"
                    aria-hidden
                  />
                  {n}
                </li>
              ))}
            </ul>
          )}
          {uploadFout && (
            <p role="alert" aria-live="polite" className="text-sm text-destructive">
              {uploadFout}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Resultaat({
  resultaat,
  token,
  titelRef,
}: {
  resultaat: RetourAanmeldAntwoord;
  token: string;
  titelRef: React.RefObject<HTMLHeadingElement>;
}) {
  const { t } = useTranslation();
  const [labelBezig, setLabelBezig] = useState(false);
  const [pakbonBezig, setPakbonBezig] = useState(false);
  const isGeenRetour = resultaat.status === "geen_retour";
  // Portaal 9W: "ontvangen" = de aanmelding kwam in een retour die al bij
  // ons binnen is. Geen label, pakbon of retouradres; dezelfde tekst als
  // beoordeling ("we bekijken je aanmelding").
  const isBeoordeling =
    resultaat.status === "beoordeling" || resultaat.status === "ontvangen";
  const isDoorgezet = Boolean(resultaat.doorgezet);

  function downloadBlob(blob: Blob, bestandsnaam: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = bestandsnaam;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function downloadLabel() {
    setLabelBezig(true);
    try {
      const blob = await retourApi.downloadLabel(
        token,
        resultaat.retournummer,
      );
      downloadBlob(blob, `retourlabel-${resultaat.retournummer}.pdf`);
    } finally {
      setLabelBezig(false);
    }
  }

  async function downloadPakbon() {
    setPakbonBezig(true);
    try {
      const blob = await retourApi.downloadPakbon(
        token,
        resultaat.retournummer,
      );
      downloadBlob(blob, `retourpakbon-${resultaat.retournummer}.pdf`);
    } finally {
      setPakbonBezig(false);
    }
  }

  // Blok 2: doorgezet naar de klantenservice. Geen weigering en (nog)
  // geen retournummer: alleen "wij kijken ernaar".
  if (isDoorgezet) {
    return (
      <div className={`${RETOUR_KAART} space-y-4`}>
        <h2 ref={titelRef} tabIndex={-1} className="text-lg font-semibold outline-none">
          {t("retour.resultaat.doorgezet_titel")}
        </h2>
        <p className="text-[14px]">{t("retour.resultaat.doorgezet_tekst")}</p>
        <p className="rounded-md bg-brand-cyan-soft p-3 text-[13.5px]">
          {t("retour.resultaat.doorgezet_hulp")}
        </p>
      </div>
    );
  }

  return (
    <div className={`${RETOUR_KAART} space-y-4`}>
      <h2 ref={titelRef} tabIndex={-1} className="text-lg font-semibold outline-none">
        {isGeenRetour
          ? t("retour.resultaat.geen_retour_titel")
          : isBeoordeling
            ? t("retour.resultaat.beoordeling_titel")
            : t("retour.resultaat.aangemeld_titel")}
      </h2>

      {isGeenRetour ? (
        <>
          <p className="text-[14px]">{t("retour.resultaat.geen_retour_tekst")}</p>
          <p className="flex items-start gap-2 rounded-md bg-brand-cyan-soft p-3 text-[13.5px]">
            <Heart className="mt-0.5 h-4 w-4 shrink-0 text-brand-cyan" aria-hidden />
            {t("retour.resultaat.geen_retour_tip")}
          </p>
          <p className="text-[13.5px] text-muted-foreground">
            {t("retour.resultaat.geen_retour_hulp")}
          </p>
        </>
      ) : (
        <>
          <p className="text-[14px]">
            {isBeoordeling
              ? t("retour.resultaat.beoordeling_tekst", {
                  nummer: resultaat.retournummer,
                })
              : t("retour.resultaat.aangemeld_tekst", {
                  nummer: resultaat.retournummer,
                })}
          </p>
          {/* Verwachtingsmanagement (designronde 29-07): het label
              komt per mail — spamfolder-check erbij. */}
          {!isBeoordeling && (
            <p className="rounded-[10px] border border-blue-100 bg-blue-50 px-4 py-3 text-[13.5px] text-[#333333]">
              {t("retour.resultaat.label_verwachting")}
            </p>
          )}
          {/* Zelfde statuslink als in de mail: direct door naar
              /RetourStatus met het langlevende statustoken. */}
          {resultaat.status_token && (
            <Link
              to={`/RetourStatus?token=${encodeURIComponent(
                resultaat.status_token,
              )}`}
              className="inline-flex items-center rounded-lg border-[1.5px] border-gray-900 bg-white px-[18px] py-2.5 text-[15px] font-bold text-gray-900 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {t("retour.resultaat.volg_link")}
            </Link>
          )}
          {resultaat.label_beschikbaar && (
            <Button
              type="button"
              className="h-11 w-full"
              onClick={downloadLabel}
              disabled={labelBezig}
            >
              {labelBezig && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              )}
              {t("retour.resultaat.label_downloaden")}
            </Button>
          )}
          {!isBeoordeling && (
            <>
              <Button
                type="button"
                variant="outline"
                className="h-11 w-full"
                onClick={downloadPakbon}
                disabled={pakbonBezig}
              >
                {pakbonBezig && (
                  <Loader2
                    className="mr-2 h-4 w-4 animate-spin"
                    aria-hidden
                  />
                )}
                {t("retour.resultaat.pakbon_downloaden")}
              </Button>
              <p className="text-[12.5px] text-muted-foreground">
                {t("retour.resultaat.pakbon_uitleg")}
              </p>
            </>
          )}
          {!isBeoordeling && resultaat.retouradres.length > 0 && (
            <address className="rounded-md border border-border bg-white p-4 text-[13.5px] not-italic">
              <span className="block font-semibold">
                {t("retour.resultaat.retouradres_titel")}
              </span>
              {resultaat.retouradres.map((regel) => (
                <span key={regel} className="block">
                  {regel}
                </span>
              ))}
            </address>
          )}
        </>
      )}
    </div>
  );
}
